/**
 * Wishlist heart on product cards.
 *
 * 1. Cards rendered client-side by Searchspring (collection + search grids) never run the
 *    Liquid card blocks, so the heart is injected here with the same markup as
 *    snippets/wishlist-card-button.liquid.
 * 2. Swish (Wishlist King) does not bind to buttons added outside its own app blocks, so clicks
 *    on `[data-wishlist-card]` buttons are handled here through the Swish window API
 *    (`window.WishlistKing`), the same state and actions its own PDP / collection buttons use:
 *    the wishlist is read with `state.observeWishlist` and items are saved / removed with
 *    `addWishlistItem / removeWishlistItem`. Cards are matched by product handle and the SDK
 *    resolves the product id itself: a Searchspring card's `data-product-id` is a variant id,
 *    so it must not be used as one. Selected state = `wk-selected` class.
 */
(() => {
  // Guards against this script's IIFE running twice on the same page (theme editor hot-reload,
  // an app embed re-injecting the tag, a section/AJAX refresh that re-runs scripts, etc). A
  // second run would register a second document click listener; since stopPropagation() does
  // not stop a second listener on the same node, every click would then double-fire and the
  // two toggle() calls would immediately cancel each other out, so nothing ever appears to work
  // after the first click.
  if (window.__wishlistCardButtonInit) return;
  window.__wishlistCardButtonInit = true;

  const CARD_SELECTOR = '.ss__result.product-card, product-card, .product-card, .resource-list__slide, .slideshow-slide';
  const BUTTON_SELECTOR = '.wishlist-collection-button [data-wishlist-card], [data-wishlist-card], .wkh-button';
  const HEART =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14" width="16" height="14" fill="none" focusable="false">' +
    '<path class="wk-heart-fill" fill="currentColor" d="M7.62943 14L6.52316 13.0082C5.23887 11.851 4.17711 10.8529 3.33787 10.0136C2.49864 9.17439 1.83106 8.42098 1.33515 7.75341C0.839237 7.08583 0.492734 6.4723 0.29564 5.91281C0.0985468 5.35332 0 4.78111 0 4.19619C0 3.00091 0.400545 2.00272 1.20163 1.20163C2.00272 0.400545 3.00091 0 4.19619 0C4.8574 0 5.48683 0.139873 6.08447 0.419619C6.68211 0.699364 7.19709 1.09355 7.62943 1.60218C8.06176 1.09355 8.57675 0.699364 9.17439 0.419619C9.77203 0.139873 10.4015 0 11.0627 0C12.2579 0 13.2561 0.400545 14.0572 1.20163C14.8583 2.00272 15.2589 3.00091 15.2589 4.19619C15.2589 4.78111 14.9632 5.91281 14.9632 5.91281C14.7661 6.4723 14.4196 7.08583 13.9237 7.75341C13.4278 8.42098 12.7602 9.17439 11.921 10.0136C11.0817 10.8529 10.02 11.851 8.73569 13.0082L7.62943 14Z"/>' +
    '<path class="wk-heart-line" fill="currentColor" d="M7.62943 14L6.52316 13.0082C5.23887 11.851 4.17711 10.8529 3.33787 10.0136C2.49864 9.17439 1.83106 8.42098 1.33515 7.75341C0.839237 7.08583 0.492734 6.4723 0.29564 5.91281C0.0985468 5.35332 0 4.78111 0 4.19619C0 3.00091 0.400545 2.00272 1.20163 1.20163C2.00272 0.400545 3.00091 0 4.19619 0C4.8574 0 5.48683 0.139873 6.08447 0.419619C6.68211 0.699364 7.19709 1.09355 7.62943 1.60218C8.06176 1.09355 8.57675 0.699364 9.17439 0.419619C9.77203 0.139873 10.4015 0 11.0627 0C12.2579 0 13.2561 0.400545 14.0572 1.20163C14.8583 2.00272 15.2589 3.00091 15.2589 4.19619C15.2589 4.78111 14.9632 5.91281 14.9632 5.91281C14.7661 6.4723 14.4196 7.08583 13.9237 7.75341C13.4278 8.42098 12.7602 9.17439 11.921 10.0136C11.0817 10.8529 10.02 11.851 8.73569 13.0082L7.62943 14ZM7.62943 11.9401C8.85014 10.8465 9.85468 9.90872 10.6431 9.1267C11.4314 8.34469 12.0545 7.6644 12.5123 7.08583C12.97 6.50727 13.2879 5.99228 13.4659 5.54087C13.644 5.08946 13.733 4.64124 13.733 4.19619C13.733 3.43324 13.4787 2.79746 12.97 2.28883C12.4614 1.7802 11.8256 1.52589 11.0627 1.52589C10.465 1.52589 9.9119 1.69437 9.40327 2.03134C8.89464 2.3683 8.54496 2.79746 8.35422 3.3188H6.90463C6.7139 2.79746 6.36421 2.3683 5.85559 2.03134C5.34696 1.69437 4.79382 1.52589 4.19619 1.52589C3.43324 1.52589 2.79746 1.7802 2.28883 2.28883C1.7802 2.79746 1.52589 3.43324 1.52589 4.19619C1.52589 4.64124 1.6149 5.08946 1.79292 5.54087C1.97094 5.99228 2.28883 6.50727 2.74659 7.08583C3.20436 7.6644 3.82743 8.34469 4.6158 9.1267C5.40418 9.90872 6.40872 10.8465 7.62943 11.9401Z"/></svg>';

  const label = (document.querySelector('script[src*="wishlist-card-button"]')?.dataset.label || 'Wishlist').trim();

  /** Wishlist item id by product handle, kept in sync with the Swish wishlist state. */
  let wishlistItemIds = new Map();

  /** @type {Promise<any> | undefined} */
  let appPromise;

  /** Resolves with `window.WishlistKing` as soon as the Swish SDK has loaded. */
  function getApp() {
    appPromise ??= new Promise((resolve) => {
      if (window.WishlistKing) {
        resolve(window.WishlistKing);
      } else {
        document.addEventListener('wk:app:ready', () => resolve(window.WishlistKing), { once: true });
      }
    });
    return appPromise;
  }

  /** @param {HTMLElement} button */
  function setSelected(button, selected) {
    button.classList.toggle('wk-selected', selected);
    button.classList.toggle('active', selected);
    button.setAttribute('aria-pressed', String(selected));
  }

  function syncSelectedStates() {
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (button.closest('.wishlist-header-link, .header-actions__wishlist-button')) return;
      const { handle } = getProductInfo(button);
      setSelected(button, Boolean(handle) && wishlistItemIds.has(handle));
    });
  }

  /**
   * Handle and variant come from the card's product link (what Swish's own collection buttons
   * use) and fall back to the button's data attributes. Never read the card's `data-product-id`.
   * @param {HTMLElement} button
   */
  function getProductInfo(button) {
    const card = button.closest(CARD_SELECTOR) || button.closest('.product-card, .ss__result, .resource-list__slide');
    const info = card && getLinkInfo(card);
    const handle = String(info?.handle || button.dataset.productHandle || '').toLowerCase();
    const variantId = String(info?.variantId || button.dataset.variantId || '');
    return { handle, variantId: variantId || undefined };
  }

  async function toggle(button) {
    try {
      const { handle, variantId } = getProductInfo(button);
      if (!handle) return;
      const app = await getApp();
      const wishlistItemId = wishlistItemIds.get(handle);
      // The heart follows the Swish wishlist state (subscription below), so nothing is set here.
      if (wishlistItemId) {
        await app.removeWishlistItem({ wishlistItemId });
      } else {
        await app.addWishlistItem({ productHandle: handle, variantId });
        showAddedToast(button);
      }
    } catch (error) {
      console.warn('[Wishlist card] toggle warning:', error);
    } finally {
      togglingButtons.delete(button);
    }
  }

  const togglingButtons = new WeakSet();

  let toastTimer;

  /**
   * Centre-bottom "Added to wishlist" message, styled as the product card (thumbnail, title).
   * Built with textContent so product titles can never inject markup.
   * @param {HTMLElement} button
   */
  function showAddedToast(button) {
    const card = button.closest(CARD_SELECTOR);
    const title = card?.querySelector('.product-grid-item__title, a.user-select-text')?.textContent?.trim() || '';
    const image = card?.querySelector('img')?.getAttribute('src') || '';

    let toast = document.getElementById('wishlist-card-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wishlist-card-toast';
      toast.className = 'wishlist-card-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML =
        '<img class="wishlist-card-toast__image" alt="" width="48" height="48" loading="eager">' +
        '<div class="wishlist-card-toast__text">' +
        '<p class="wishlist-card-toast__title"></p>' +
        '<p class="wishlist-card-toast__status">Added to wishlist</p>' +
        '</div>';
      document.body.appendChild(toast);
    }

    const img = toast.querySelector('.wishlist-card-toast__image');
    if (image) {
      img.src = image;
      img.hidden = false;
    } else {
      img.removeAttribute('src');
      img.hidden = true;
    }
    toast.querySelector('.wishlist-card-toast__title').textContent = title;

    // Force a reflow so repeated adds restart the enter transition.
    void toast.offsetWidth;
    toast.classList.add('is-visible');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3000);
  }

  // Swish renders its own "Added to wishlist" box bottom-right. Hide that box so only the
  // centred message above is shown. Matched by text, so it is only hidden when the text is found
  // in a fixed-position element that is not part of this theme's toast.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement) || node.id === 'wishlist-card-toast') continue;
        if (!/added to wishlist/i.test(node.textContent || '')) continue;
        let el = node;
        while (el && el !== document.body) {
          if (getComputedStyle(el).position === 'fixed') {
            el.style.setProperty('display', 'none', 'important');
            break;
          }
          el = el.parentElement;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
    if (!(button instanceof HTMLElement)) return;
    if (button.closest('.wishlist-header-link, .header-actions__wishlist-button')) return;
    event.preventDefault();
    // stopImmediatePropagation (not just stopPropagation) so that if this listener ever ends up
    // registered more than once, a single click can't double-fire and cancel itself out.
    event.stopImmediatePropagation();
    event.stopPropagation();
    if (togglingButtons.has(button)) return;
    togglingButtons.add(button);
    toggle(button);
  });

  /** @param {Element} card */
  function getLinkInfo(card) {
    const link = card.querySelector('.card-gallery a[href*="/products/"], a[href*="/products/"]');
    if (!link) return null;
    const url = new URL(link.getAttribute('href'), window.location.origin);
    const match = url.pathname.match(/\/products\/([^/?#]+)/);
    return match ? { handle: decodeURIComponent(match[1]), variantId: url.searchParams.get('variant') || '' } : null;
  }

  /**
   * Put the heart on the title row (client design): a zero-height relative wrapper placed right
   * before the title link, with the button absolutely positioned to the far right of the title row.
   * @param {HTMLElement} wrapper
   * @param {Element} content
   */
  function placeOnTitleRow(wrapper, content) {
    const title = content.querySelector(':scope > a.user-select-text, :scope a.user-select-text');
    if (!title) return;
    if (wrapper.nextElementSibling !== title) title.before(wrapper);
    wrapper.classList.add('wishlist-collection-button--title');
    content.classList.add('product-card__content--wishlist');
  }

  /** @param {Element} card */
  function enhance(card) {
    const content = card.querySelector('.product-card__content') || card;
    let wrapper = content.querySelector('.wishlist-collection-button');

    if (!wrapper) {
      const gallery = card.querySelector('.card-gallery') || card.querySelector('.product-media-container') || card;
      const info = getLinkInfo(card);
      if (!gallery || !info) return;

      const title = card.querySelector('.product-grid-item__title, [class*="title"]')?.textContent?.trim();

      wrapper = document.createElement('div');
      wrapper.className = 'wishlist-collection-button';
      wrapper.innerHTML =
        '<button type="button" class="wkh-button wkh-align-center wkh-align-content-center" data-wishlist-card aria-pressed="false">' +
        '<span class="wkh-icon" icon="wishlist" aria-hidden="true">' + HEART + '</span></button>';

      const button = wrapper.querySelector('button');
      button.dataset.productHandle = info.handle;
      if (info.variantId) button.dataset.variantId = info.variantId;
      button.setAttribute('aria-label', title ? `${label}: ${title}` : label);
      gallery.appendChild(wrapper);
    } else {
      const button = wrapper.querySelector('[data-wishlist-card], .wkh-button');
      if (button) {
        const iconSpan = button.querySelector('.wkh-icon');
        if (iconSpan && !iconSpan.querySelector('.wk-heart-fill')) {
          iconSpan.innerHTML = HEART;
        }
        if (!button.dataset.productHandle || !button.dataset.variantId) {
          const info = getLinkInfo(card);
          if (info) {
            if (!button.dataset.productHandle && info.handle) button.dataset.productHandle = info.handle;
            if (!button.dataset.variantId && info.variantId) button.dataset.variantId = info.variantId;
          }
        }
      }
    }

    placeOnTitleRow(wrapper, content);
  }

  let timer;
  function scan() {
    timer = undefined;
    document.querySelectorAll(CARD_SELECTOR).forEach(enhance);
    syncSelectedStates();
  }
  function queueScan() {
    if (timer) return;
    timer = setTimeout(scan, 50);
  }

  // Swish ignores add / remove calls while a previous action is pending and only a state
  // subscriber's refetch clears that, so this subscription is also what lets a second heart be
  // saved on the same page.
  getApp().then((app) => {
    app.state.observeWishlist({ wishlistId: 'mine' }).subscribe((wishlist) => {
      wishlistItemIds = new Map(wishlist.items.map((item) => [String(item.product.handle).toLowerCase(), item.id]));
      syncSelectedStates();
    });
  }).catch((error) => console.warn('[Wishlist card] could not read the Swish wishlist state:', error));

  new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true });
  queueScan();
})();
